import { Curve, BarsH, BarsV, Donut, Stack100, Kpis, Panel } from '../components/charts';
import type { ModuleKey } from '../module';

/* ============================================================
   Sepuluh dasbor, satu per modul.

   Every number below is invented. It is shaped like a 48 MW data
   centre eighteen months in — plausible spreads, plausible slippage,
   a forecast that has crept over budget — because a mockup filled
   with round, healthy numbers hides exactly the layouts that matter.
   The interesting question a dashboard has to answer is never "how
   many are there" but "which one is holding up the field", so each
   one ends in a table naming names.

   Nothing here calls the API. That is what makes it safe to serve
   these without a login.
   ============================================================ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];

/* Ordered things use the ordinal ramp; categories use the series set. */
const RAMP = ['var(--ramp-1)', 'var(--ramp-2)', 'var(--ramp-3)', 'var(--ramp-4)'];
const S1 = 'var(--series-1)';
const S2 = 'var(--series-2)';
const S3 = 'var(--series-3)';

type Col = { head: string; cell: (r: string[], i: number) => React.ReactNode; num?: boolean };

function Table({ cols, rows }: { cols: Col[]; rows: string[][] }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.head}>{c.head}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r[0]! + i} className="no-click">
              {cols.map((c, ci) => (
                <td key={c.head} className={c.num ? 'num' : undefined}>
                  {c.cell(r, ci)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Most tables here are plain text with the first column emphasised. */
function plain(heads: string[]): Col[] {
  return heads.map((h, i) => ({
    head: h,
    num: i === 0,
    cell: (r) => r[i],
  }));
}

function chipCol(heads: string[], chipAt: number, tone: (v: string) => string): Col[] {
  return heads.map((h, i) => ({
    head: h,
    num: i === 0,
    cell: (r) => (i === chipAt ? <span className={`chip ${tone(r[i]!)}`}>{r[i]}</span> : r[i]),
  }));
}

/* ============================================================
   PM — Document Control
   ============================================================ */

function PmDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Documents in register', value: '1,284' },
          { label: 'Awaiting review', value: '96', note: '23 past their due date', tone: 'warn' },
          { label: 'Returned this month', value: '23', note: 'Code 3 or worse' },
          { label: 'Median turnaround', value: '4.2 d', note: 'against a 7 day target', tone: 'good' },
        ]}
      />

      <div className="grid-2">
        <Panel
          title="Submitted and closed out"
          note="A register only stays honest while the two lines track each other."
        >
          <Curve
            labels={MONTHS}
            series={[
              { name: 'Submitted', points: [86, 102, 118, 131, 127, 140, 152, 147], color: S1, kind: 'actual' },
              { name: 'Closed out', points: [71, 95, 104, 126, 119, 133, 141, 138], color: S2 },
            ]}
          />
        </Panel>

        <Panel title="Review outcome mix" note="All codes issued this year. Ordered, so the ramp is the right palette.">
          <Stack100
            slices={[
              { name: 'Code 1 — approved', value: 412, color: RAMP[0]! },
              { name: 'Code 2 — approved as noted', value: 508, color: RAMP[1]! },
              { name: 'Code 3 — revise and resubmit', value: 231, color: RAMP[2]! },
              { name: 'Code 4 — rejected', value: 63, color: RAMP[3]! },
            ]}
          />
          <p className="panel-foot">
            Code 3 and 4 together are 23% of all reviews. Every one of them sent a document back
            round the loop.
          </p>
        </Panel>
      </div>

      <Panel title="Turnaround by discipline" note="Median days from submission to a review code. Target is 7 days." wide>
        <BarsH
          suffix=" d"
          againstLabel="Target 7 d"
          valueLabel="Median actual"
          rows={[
            { label: 'Civil', value: 3.8, against: 7, tone: 'good' },
            { label: 'Structural', value: 4.6, against: 7, tone: 'good' },
            { label: 'Mechanical', value: 5.1, against: 7, tone: 'good' },
            { label: 'Electrical', value: 6.9, against: 7, tone: 'accent' },
            { label: 'ICT', value: 8.4, against: 7, tone: 'warn' },
            { label: 'Fire & life safety', value: 9.2, against: 7, tone: 'crit' },
          ]}
        />
      </Panel>

      <Panel title="Waiting longest" note="Not a count. The five documents someone has to chase today." wide>
        <Table
          cols={chipCol(
            ['Document', 'Title', 'With', 'Waiting', 'Stage'],
            4,
            () => 'blue',
          )}
          rows={[
            ['DC-EL-0421', 'MV switchgear single line diagram', 'Electrical lead', '19 days', 'IFA'],
            ['DC-FA-0118', 'Fire detection cause & effect matrix', 'Fire consultant', '17 days', 'IFR'],
            ['DC-IT-0233', 'Structured cabling containment layout', 'ICT reviewer', '14 days', 'IFD'],
            ['DC-ME-0507', 'Chilled water schematic — hall B', 'Mechanical lead', '12 days', 'IFA'],
            ['DC-CV-0090', 'Perimeter drainage details', 'Civil reviewer', '11 days', 'IFD'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   ENG — Engineering Tracking
   ============================================================ */

function EngDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Deliverables', value: '642' },
          { label: 'Issued for construction', value: '318', note: '49% of the register' },
          { label: 'Overdue reviews', value: '17', note: 'blocking 6 field activities', tone: 'crit' },
          { label: 'Revisions to reach IFC', value: '1.9', note: 'average across all disciplines' },
        ]}
      />

      <div className="grid-2">
        <Panel title="The ladder" note="How far the register has climbed. Each rung is a subset of the one above.">
          <BarsH
            rows={[
              { label: 'IFR — issued for review', value: 642, color: RAMP[0] },
              { label: 'IFD — issued for design', value: 561, color: RAMP[1] },
              { label: 'IFA — issued for approval', value: 447, color: RAMP[2] },
              { label: 'IFC — issued for construction', value: 318, color: RAMP[3] },
            ]}
          />
          <p className="panel-foot">
            324 deliverables have not reached IFC. That number, not the total, is the programme risk.
          </p>
        </Panel>

        <Panel title="Issued against plan" note="Thin line is the plan. Solid line is what was actually issued.">
          <Curve
            labels={MONTHS}
            series={[
              { name: 'Planned', points: [60, 140, 230, 320, 410, 500, 580, 642], color: S3, kind: 'planned' },
              { name: 'Actual', points: [52, 121, 198, 281, 352, 411, 455, 472], color: S1, kind: 'actual' },
            ]}
          />
          <p className="panel-foot">170 deliverables behind plan, and the gap has widened every month since April.</p>
        </Panel>
      </div>

      <Panel title="By discipline" note="Registered against reached IFC. Structural calculation sits inside these, not beside them." wide>
        <BarsH
          againstLabel="Registered"
          valueLabel="At IFC"
          rows={[
            { label: 'Civil', value: 98, against: 148 },
            { label: 'Structural', value: 76, against: 121 },
            { label: 'Mechanical', value: 61, against: 134 },
            { label: 'Electrical', value: 49, against: 118 },
            { label: 'ICT', value: 26, against: 71 },
            { label: 'Fire & life safety', value: 8, against: 50 },
          ]}
        />
      </Panel>

      <Panel title="Holding up the field" note="Deliverables with construction waiting on them." wide>
        <Table
          cols={chipCol(
            ['Deliverable', 'Discipline', 'Stage', 'Late by', 'Waiting on it'],
            2,
            () => 'warn',
          )}
          rows={[
            ['EN-FA-0044', 'Fire & life safety', 'IFA', '21 days', 'Hall B ceiling close-up'],
            ['EN-EL-0311', 'Electrical', 'IFA', '16 days', 'Busbar first fix'],
            ['EN-ME-0187', 'Mechanical', 'IFD', '13 days', 'CHW pipe route hall C'],
            ['EN-ST-0402', 'Structural', 'IFC', '9 days', 'Generator plinth pour'],
            ['EN-IT-0129', 'ICT', 'IFA', '8 days', 'Containment above racks'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   PROG — Progress Assurance
   ============================================================ */

function ProgDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Verified progress', value: '61.4%' },
          { label: 'Reported by contractor', value: '68.0%' },
          { label: 'Gap', value: '−6.6 pts', note: 'worth $12.1M of certification', tone: 'crit' },
          { label: 'Areas verified', value: '18 / 24', note: 'this reporting period' },
        ]}
      />

      <Panel
        title="Planned, reported, verified"
        note="Three lines, deliberately. The one that matters is the distance between the last two."
        wide
      >
        <Curve
          suffix="%"
          labels={MONTHS}
          series={[
            { name: 'Planned', points: [8, 18, 30, 42, 54, 66, 76, 84], color: S3, kind: 'planned' },
            { name: 'Reported', points: [7, 16, 27, 38, 49, 58, 64, 68], color: S2 },
            { name: 'Verified', points: [6, 14, 24, 34, 43, 52, 57, 61.4], color: S1, kind: 'actual' },
          ]}
        />
      </Panel>

      <div className="grid-2">
        <Panel title="By area" note="Reported behind, verified in front. Where they separate, someone claimed early.">
          <BarsH
            suffix="%"
            againstLabel="Reported"
            valueLabel="Verified"
            rows={[
              { label: 'Admin building', value: 92, against: 93 },
              { label: 'Utility yard', value: 88, against: 90 },
              { label: 'Data hall A', value: 78, against: 82 },
              { label: 'Perimeter', value: 71, against: 74 },
              { label: 'Data hall B', value: 64, against: 71 },
              { label: 'Substation', value: 55, against: 66 },
              { label: 'Data hall C', value: 41, against: 52 },
              { label: 'Chiller plant', value: 33, against: 47 },
            ]}
          />
        </Panel>

        <Panel title="Verification status" note="An unverified area is not progress. It is a claim.">
          <Donut
            centre="18"
            centreNote="of 24 verified"
            slices={[
              { name: 'Verified in full', value: 18, color: S3 },
              { name: 'Partially verified', value: 4, color: S2 },
              { name: 'Not yet visited', value: 2, color: 'var(--text-muted)' },
            ]}
          />
        </Panel>
      </div>

      <Panel title="Largest gaps" note="Where the contractor's claim and the field walk disagree most." wide>
        <Table
          cols={plain(['Area', 'Reported', 'Verified', 'Gap', 'Reason recorded on the walk'])}
          rows={[
            ['Chiller plant', '47%', '33%', '−14 pts', 'Pipework delivered but not installed'],
            ['Data hall C', '52%', '41%', '−11 pts', 'Containment counted before fixing'],
            ['Substation', '66%', '55%', '−11 pts', 'Cable pulled, not terminated'],
            ['Data hall B', '71%', '64%', '−7 pts', 'Ceiling grid partially complete'],
            ['Data hall A', '82%', '78%', '−4 pts', 'Snagging outstanding'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   COSTM — Cost Assurance
   ============================================================ */

function CostDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Approved budget', value: '$184.0M' },
          { label: 'Committed', value: '$151.2M', note: '82% of budget let' },
          { label: 'Certified to date', value: '$96.4M', note: 'against verified progress' },
          { label: 'Forecast at completion', value: '$189.7M', note: '+3.1% over budget', tone: 'crit' },
        ]}
      />

      <div className="grid-2">
        <Panel title="Where the budget stands" note="Certified is the only one of these three that reflects work in the ground.">
          <Stack100
            suffix="M"
            slices={[
              { name: 'Certified', value: 96.4, color: S1 },
              { name: 'Committed, not certified', value: 54.8, color: S2 },
              { name: 'Uncommitted', value: 32.8, color: 'var(--text-muted)' },
            ]}
          />
          <p className="panel-foot">
            Only $32.8M is still uncommitted. Every forecast movement from here has to come out of
            that, or out of the contingency.
          </p>
        </Panel>

        <Panel title="Forecast variance" note="Percentage over the approved budget, month by month.">
          <Curve
            suffix="%"
            labels={MONTHS}
            series={[
              { name: 'Forecast variance', points: [0.4, 0.7, 1.1, 1.4, 1.9, 2.4, 2.8, 3.1], color: S2, kind: 'actual' },
            ]}
          />
          <p className="panel-foot">Eight consecutive months of drift. No month reversed it.</p>
        </Panel>
      </div>

      <Panel title="By package" note="Certified against budget. A short bar this late is either underclaimed or undelivered." wide>
        <BarsH
          suffix="M"
          againstLabel="Budget"
          valueLabel="Certified"
          rows={[
            { label: 'MEP installation', value: 33.1, against: 52.0 },
            { label: 'Civil works', value: 28.4, against: 41.0 },
            { label: 'Electrical fit-out', value: 12.6, against: 24.0 },
            { label: 'Cooling', value: 9.8, against: 19.5 },
            { label: 'Structure', value: 8.2, against: 12.0 },
            { label: 'ICT', value: 2.4, against: 9.5 },
            { label: 'Fire & life safety', value: 1.6, against: 6.5 },
            { label: 'Preliminaries', value: 0.3, against: 19.5 },
          ]}
        />
      </Panel>

      <Panel title="Driving the overrun" note="Packages carrying the forecast movement, largest first." wide>
        <Table
          cols={chipCol(['Package', 'Budget', 'Forecast', 'Variance', 'Principal cause'], 3, (v) =>
            v.startsWith('+') ? 'crit' : 'good',
          )}
          rows={[
            ['Electrical fit-out', '$24.0M', '$27.1M', '+$3.1M', 'Switchgear rework after design change'],
            ['Cooling', '$19.5M', '$21.3M', '+$1.8M', 'CHW route revised twice'],
            ['ICT', '$9.5M', '$10.4M', '+$0.9M', 'Containment scope grew with rack count'],
            ['Fire & life safety', '$6.5M', '$7.0M', '+$0.5M', 'Late authority comments'],
            ['Structure', '$12.0M', '$11.2M', '−$0.8M', 'Piling favourable against provisional sum'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   QM — Quality Management
   ============================================================ */

function QmDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'ITPs raised', value: '412' },
          { label: 'Passed first time', value: '78%', note: 'against an 85% target', tone: 'warn' },
          { label: 'Open NCRs', value: '34', note: '6 blocking certification', tone: 'crit' },
          { label: 'Oldest open NCR', value: '62 d', note: 'raised 12 June', tone: 'crit' },
        ]}
      />

      <div className="grid-2">
        <Panel title="Inspection outcomes" note="Every ITP closed this year.">
          <Stack100
            slices={[
              { name: 'Passed', value: 322, color: S3 },
              { name: 'Passed with comment', value: 54, color: S2 },
              { name: 'Failed', value: 36, color: 'var(--status-critical)' },
            ]}
          />
          <p className="panel-foot">
            A failed ITP blocks certification here rather than surfacing at handover. That is the
            entire point of holding the gate on the same activity that carries cost.
          </p>
        </Panel>

        <Panel title="First-time pass rate" note="Thin line is the target. Solid line is what the site achieved.">
          <Curve
            suffix="%"
            labels={MONTHS}
            series={[
              { name: 'Target', points: [85, 85, 85, 85, 85, 85, 85, 85], color: S3, kind: 'planned' },
              { name: 'Achieved', points: [71, 73, 72, 76, 77, 79, 78, 78], color: S1, kind: 'actual' },
            ]}
          />
        </Panel>
      </div>

      <div className="grid-2">
        <Panel title="NCR ageing" note="How long open non-conformances have been open.">
          <BarsH
            rows={[
              { label: '0 – 15 days', value: 11, tone: 'good' },
              { label: '16 – 30 days', value: 9, tone: 'accent' },
              { label: '31 – 60 days', value: 8, tone: 'warn' },
              { label: 'Over 60 days', value: 6, tone: 'crit' },
            ]}
          />
        </Panel>

        <Panel title="Open NCRs by discipline" note="Where quality effort is actually needed this month.">
          <BarsH
            rows={[
              { label: 'Mechanical', value: 11 },
              { label: 'Electrical', value: 9 },
              { label: 'Civil', value: 6 },
              { label: 'Structural', value: 4 },
              { label: 'Fire & life safety', value: 3 },
              { label: 'ICT', value: 1 },
            ]}
          />
        </Panel>
      </div>

      <Panel title="Blocking certification" note="Open non-conformances sitting on activities that cannot be paid." wide>
        <Table
          cols={chipCol(['NCR', 'Raised against', 'Discipline', 'Age', 'Severity'], 4, (v) =>
            v === 'Major' ? 'crit' : 'warn',
          )}
          rows={[
            ['NCR-0088', 'CHW pipe support spacing, hall B', 'Mechanical', '62 days', 'Major'],
            ['NCR-0104', 'Cable tray earth continuity', 'Electrical', '48 days', 'Major'],
            ['NCR-0117', 'Concrete cover to rebar, plinth 4', 'Structural', '39 days', 'Major'],
            ['NCR-0131', 'Fire stopping at hall C penetrations', 'Fire & life safety', '31 days', 'Minor'],
            ['NCR-0142', 'Drainage fall below specification', 'Civil', '22 days', 'Minor'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   DELAY — Forensic Delay Analysis
   ============================================================ */

function DelayDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Contract duration', value: '640 d' },
          { label: 'As-built to date', value: '731 d' },
          { label: 'Total slip', value: '91 d', note: 'against the contract completion date', tone: 'crit' },
          { label: 'Assessed excusable', value: '54 d', note: '37 days remain at contractor risk' },
        ]}
      />

      <Panel
        title="Window analysis"
        note="Slip measured window by window against the programme in force at the time — not against one baseline read backwards."
        wide
      >
        <BarsH
          suffix=" d"
          rows={[
            { label: 'W1  Feb – Mar', value: 6, tone: 'good' },
            { label: 'W2  Apr – May', value: 14, tone: 'accent' },
            { label: 'W3  Jun – Jul', value: 23, tone: 'crit' },
            { label: 'W4  Aug – Sep', value: 19, tone: 'warn' },
            { label: 'W5  Oct – Nov', value: 21, tone: 'crit' },
            { label: 'W6  Dec – Jan', value: 8, tone: 'accent' },
          ]}
        />
      </Panel>

      <div className="grid-2">
        <Panel title="Cause of delay" note="Days attributed, across all six windows.">
          <Donut
            centre="91 d"
            centreNote="total slip"
            slices={[
              { name: 'Design change', value: 31, color: S1 },
              { name: 'Late access', value: 22, color: S2 },
              { name: 'Subcontractor', value: 16, color: S3 },
              { name: 'Owner instruction', value: 14, color: 'var(--ramp-2)' },
              { name: 'Weather', value: 8, color: 'var(--text-muted)' },
            ]}
          />
        </Panel>

        <Panel title="Entitlement" note="Time, money, or neither. The three categories that decide who pays.">
          <Stack100
            suffix=" d"
            slices={[
              { name: 'Excusable & compensable', value: 34, color: S1 },
              { name: 'Excusable only', value: 20, color: S2 },
              { name: 'Non-excusable', value: 37, color: 'var(--text-muted)' },
            ]}
          />
          <p className="panel-foot">
            The analysis ends in a mitigation plan built on the recovery actually available — not
            the recovery that would be convenient.
          </p>
        </Panel>
      </div>

      <Panel title="Driving activity by window" note="What was on the critical path while each window's slip accrued." wide>
        <Table
          cols={chipCol(['Window', 'Driving activity', 'Slip', 'Cause', 'Entitlement'], 4, (v) =>
            v === 'Non-excusable' ? 'warn' : 'blue',
          )}
          rows={[
            ['W3  Jun – Jul', 'MV switchgear delivery', '23 d', 'Design change', 'Excusable & compensable'],
            ['W5  Oct – Nov', 'Hall B ceiling close-up', '21 d', 'Late access', 'Excusable & compensable'],
            ['W4  Aug – Sep', 'CHW pipework hall C', '19 d', 'Subcontractor', 'Non-excusable'],
            ['W2  Apr – May', 'Generator plinth pour', '14 d', 'Owner instruction', 'Excusable only'],
            ['W6  Dec – Jan', 'Perimeter drainage', '8 d', 'Weather', 'Excusable only'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   HSE — Manpower Tracking
   ============================================================ */

const DAYS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14'];

function HseDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'On site now', value: '1,142', note: 'across 9 contractors' },
          { label: 'Man-hours this month', value: '214,880', note: 'counted at the gate, not typed in' },
          { label: 'Days without a lost-time injury', value: '187', tone: 'good' },
          { label: 'Certificates expiring ≤ 14 days', value: '23', note: '4 already expired', tone: 'crit' },
        ]}
      />

      <Panel
        title="Through the gate"
        note="Headcount in and out, by day. The two bars should match by the end of every shift — where they do not, someone is unaccounted for."
        wide
      >
        <BarsV
          labels={DAYS}
          aName="In"
          bName="Out"
          a={[1180, 1204, 1191, 1166, 1210, 848, 402, 1173, 1198, 1221, 1187, 1205, 861, 388]}
          b={[1180, 1204, 1189, 1166, 1210, 848, 402, 1173, 1196, 1221, 1187, 1205, 861, 388]}
        />
        <p className="panel-foot">
          Two exceptions in fourteen days — day 03 and day 09 — each closed out by the HSE officer
          the following morning. Weekends carry the reduced shift.
        </p>
      </Panel>

      <div className="grid-2">
        <Panel title="Man-hours against plan" note="Cumulative. This is the denominator of every safety statistic on the project.">
          <Curve
            labels={MONTHS}
            series={[
              { name: 'Planned', points: [92000, 198000, 318000, 452000, 596000, 742000, 884000, 1020000], color: S3, kind: 'planned' },
              { name: 'Actual', points: [88000, 191000, 309000, 441000, 588000, 731000, 878000, 1014000], color: S1, kind: 'actual' },
            ]}
          />
        </Panel>

        <Panel title="Workforce by contractor" note="Nine on site today. The top three carry two thirds of the exposure.">
          <Donut
            centre="1,142"
            centreNote="on site now"
            slices={[
              { name: 'MEP subcontractor', value: 402, color: S1 },
              { name: 'Civil works', value: 268, color: S2 },
              { name: 'Electrical', value: 191, color: S3 },
              { name: 'Structure', value: 124, color: 'var(--ramp-2)' },
              { name: 'Six others', value: 157, color: 'var(--text-muted)' },
            ]}
          />
        </Panel>
      </div>

      <Panel title="Certificate expiry" note="Checked at the gate rather than in a folder. A lapsed card stops the turnstile." wide>
        <BarsH
          rows={[
            { label: 'Already expired', value: 4, tone: 'crit' },
            { label: 'Expires within 7 days', value: 9, tone: 'crit' },
            { label: 'Expires in 8 – 14 days', value: 14, tone: 'warn' },
            { label: 'Expires in 15 – 30 days', value: 38, tone: 'accent' },
            { label: 'Valid beyond 30 days', value: 1077, tone: 'good' },
          ]}
        />
      </Panel>

      <Panel title="Gate exceptions" note="Open items an HSE officer has to close before the next shift." wide>
        <Table
          cols={chipCol(['Reference', 'Person', 'Contractor', 'Issue', 'Status'], 4, (v) =>
            v === 'Access blocked' ? 'crit' : 'warn',
          )}
          rows={[
            ['GX-0412', 'Confined space ticket', 'MEP subcontractor', 'Expired 3 days ago', 'Access blocked'],
            ['GX-0413', 'Working at height card', 'Electrical', 'Expires tomorrow', 'Notice sent'],
            ['GX-0417', 'Site induction', 'Six others', 'Not on record', 'Access blocked'],
            ['GX-0421', 'Hot work permit holder', 'Structure', 'Expires in 4 days', 'Notice sent'],
            ['GX-0424', 'Unmatched gate record', 'Civil works', 'In without an out on day 09', 'Closed out'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   PROC — Procurement
   ============================================================ */

function ProcDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Packages', value: '15' },
          { label: 'PO issued', value: '11', note: '2 held on material approval', tone: 'warn' },
          { label: 'Delivered in full', value: '6', note: 'of 11 ordered' },
          { label: 'Worst slip to site', value: '9 d', note: 'BQ F2, F6-F12 — instruments', tone: 'crit' },
        ]}
      />

      <div className="grid-2">
        <Panel
          title="Where each package sits"
          note="Seven steps from purchase order to material accepted on site."
        >
          <BarsV
            labels={['PO', 'Mfg', 'FAT', 'Del', 'Insp', 'Store', 'On site']}
            a={[15, 13, 9, 8, 7, 6, 6]}
            aName="Packages reaching this step"
          />
          <p className="panel-foot">
            Two packages have not left the first column because their material approval is still
            with the reviewer. Procurement is not late here — engineering is.
          </p>
        </Panel>

        <Panel title="Approval delay carried into delivery" note="Days a late MAR pushed the on-site date.">
          <BarsH
            rows={[
              { label: 'BQ F2, F6-F12', value: 9, tone: 'crit' },
              { label: 'BQ E8-E11', value: 6, tone: 'warn' },
              { label: 'BQ H1', value: 4, tone: 'warn' },
              { label: 'BQ G19', value: 2, tone: 'accent' },
              { label: 'BQ E4-E7', value: 0, tone: 'good' },
            ]}
          />
          <p className="panel-foot">
            Measured, not estimated: approval landed n working days after plan, so the order moved
            n working days. Whether that reaches installation depends on the float behind it.
          </p>
        </Panel>
      </div>

      <Panel
        title="Deliveries against ordered quantity"
        note="Multiple delivery notes are normal. What is short stays short until it arrives."
        wide
      >
        <Table
          cols={chipCol(['Package', 'Ordered', 'Delivered', 'Short', 'Notes', 'Status'], 5, (v) =>
            v === 'Complete' ? 'good' : v === 'Blocked' ? 'crit' : 'warn',
          )}
          rows={[
            ['BQ E4-E7 · ACP panels, LV rooms', '4 set', '4 set', '—', '2', 'Complete'],
            ['BQ E12 · Chiller network switch', '5 set', '5 set', '—', '1', 'Complete'],
            ['BQ F1 · TH sensor Vaisala HM92', '23 no', '18 no', '5 no', '3', 'Part delivered'],
            ['BQ G19 · TH sensor cabling point', '30 item', '22 item', '8 item', '2', 'Part delivered'],
            ['BQ F2, F6-F12 · CHW instruments', '35 no', '0 no', '35 no', '0', 'Blocked'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   CX — Commissioning
   ============================================================ */

function CxDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'Equipment registered', value: '366' },
          { label: 'CxL2 passed', value: '241', note: '66% of the register' },
          { label: 'Held by open NCR', value: '17', note: 'cannot advance a level', tone: 'crit' },
          { label: 'Passed without report', value: '0', note: 'the status will not move without one', tone: 'good' },
        ]}
      />

      <div className="grid-2">
        <Panel title="Progress by level" note="Equipment cannot climb until the level below it passes.">
          <BarsH
            rows={[
              { label: 'CxL1 · FAT', value: 100, tone: 'good' },
              { label: 'CxL2 · QA/QC', value: 66, tone: 'accent' },
              { label: 'CxL3 · Pre-functional', value: 38, tone: 'warn' },
              { label: 'CxL4 · Functional', value: 12, tone: 'warn' },
              { label: 'CxL5 · IST', value: 0, tone: 'crit' },
            ]}
            suffix="%"
          />
        </Panel>

        <Panel title="Test outcomes recorded" note="Every result carries an uploaded report.">
          <Stack100
            slices={[
              { name: 'Passed', value: 241, color: S3 },
              { name: 'Passed with punch', value: 48, color: S2 },
              { name: 'Failed', value: 31, color: 'var(--status-critical)' },
            ]}
          />
          <p className="panel-foot">
            A failed test raises an NCR against the same equipment tag, and that tag stops climbing
            until the NCR closes. The gate is the product.
          </p>
        </Panel>
      </div>

      <Panel
        title="By floor and room"
        note="366 tags is too many to read flat, so the table groups, filters and folds."
        wide
      >
        <Table
          cols={chipCol(['Location', 'Equipment', 'CxL2', 'CxL3', 'CxL4', 'State'], 5, (v) =>
            v === 'On track' ? 'good' : v === 'Held' ? 'crit' : 'warn',
          )}
          rows={[
            ['5F · S510 Datahall', '58 tags', '58 / 58', '31 / 58', '9 / 58', 'On track'],
            ['5F · S520 Datahall', '54 tags', '49 / 54', '20 / 54', '4 / 54', 'Behind'],
            ['5F · LV Room A–D', '36 tags', '30 / 36', '11 / 36', '0 / 36', 'Behind'],
            ['5F · IDF Room A / B', '22 tags', '22 / 22', '14 / 22', '6 / 22', 'On track'],
            ['RF · Chiller Area', '41 tags', '24 / 41', '5 / 41', '0 / 41', 'Held'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================
   TEAM — Resource Control
   ============================================================ */

function TeamDashboard() {
  return (
    <>
      <Kpis
        items={[
          { label: 'People mapped', value: '31' },
          { label: 'Active this week', value: '24', note: 'across 63 activities' },
          { label: 'Carrying 5 or more', value: '4', note: 'concurrent activities', tone: 'warn' },
          { label: 'Activities with no PIC', value: '112', note: 'nobody to ask', tone: 'crit' },
        ]}
      />

      <div className="grid-2">
        <Panel title="Load this week" note="Concurrent activities per person. Five is where quality starts to slip.">
          <BarsH
            rows={[
              { label: 'Site supervisor — Datahall', value: 8, tone: 'crit' },
              { label: 'Cabling crew lead A', value: 6, tone: 'crit' },
              { label: 'Instrument technician 1', value: 5, tone: 'warn' },
              { label: 'Panel technician 2', value: 4, tone: 'accent' },
              { label: 'Commissioning engineer 1', value: 3, tone: 'good' },
            ]}
          />
        </Panel>

        <Panel title="Coverage" note="Activities that have a named person against them.">
          <Donut
            slices={[
              { name: 'Named PIC', value: 254, color: S3 },
              { name: 'Crew only', value: 112, color: 'var(--status-critical)' },
            ]}
            centre="69%"
            centreNote="named"
          />
          <p className="panel-foot">
            The programme names crews, not people. The 112 without a name are not a reporting gap —
            they are activities nobody can be asked about on a Monday morning.
          </p>
        </Panel>
      </div>

      <Panel title="This week, by person" note="What each named person is carrying between Monday and Saturday." wide>
        <Table
          cols={chipCol(['Person', 'Role', 'Activities', 'Location', 'Late', 'State'], 5, (v) =>
            v === 'Clear' ? 'good' : v === 'Overloaded' ? 'crit' : 'warn',
          )}
          rows={[
            ['Site supervisor — Datahall', 'BMS Site Supervisor', '8', '5F Datahall', '2', 'Overloaded'],
            ['Cabling crew lead A', 'BMS Cabling Crew', '6', '5F LV Room A–D', '1', 'Overloaded'],
            ['Instrument technician 1', 'BMS Instrument Tech', '5', 'RF Chiller Area', '1', 'Watch'],
            ['Panel technician 2', 'BMS Panel Technician', '4', '5F IDF Room A', '0', 'Clear'],
            ['Commissioning engineer 1', 'BMS Cx Engineer', '3', '5F S510 Datahall', '0', 'Clear'],
          ]}
        />
      </Panel>
    </>
  );
}

/* ============================================================ */

const BY_MODULE: Record<ModuleKey, () => JSX.Element> = {
  PM: PmDashboard,
  ENG: EngDashboard,
  PROC: ProcDashboard,
  PROG: ProgDashboard,
  CX: CxDashboard,
  COSTM: CostDashboard,
  QM: QmDashboard,
  DELAY: DelayDashboard,
  TEAM: TeamDashboard,
  HSE: HseDashboard,
};

export function Dashboard({ module }: { module: ModuleKey }) {
  const View = BY_MODULE[module];
  return <View />;
}
